/**
 * Raja Sleem Portfolio - Main App Component
 *
 * Status: [IMPLEMENTED]
 * Task: T1.3 - React Landing Page (refactored to chat-first)
 * Task: T2.8.1 - Admin routes added
 *
 * Layout: Tab-based navigation
 * - Chat (default) - Main focus, the product
 * - About Raja - Background info, secondary
 *
 * Admin routes (hidden, not linked from public navigation):
 * - /azzmin-access - Resume generator
 * - /azzmin-login - Admin login
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChatContainer } from './components/Chat';
import About from './components/About';
import WhatIBring from './components/WhatIBring';
import SleemAI from './components/SleemAI';
import ThemeToggle from './components/ThemeToggle';
import { ThemeProvider } from './context/ThemeContext';
import { VERSION } from './config/version';
import { trackPageView } from './utils/track';
import memoji from './raja-memoji.png';

// Admin pages (lazy loaded to keep main bundle small)
import AdminLoginPage from './pages/AdminLoginPage';
import AdminShell from './pages/AdminShell';

/**
 * Path-aware initial tab selection (F3.5).
 *
 * After F3.4 the FastAPI server returns the React shell for the explicit
 * routes /chat and /about (and the admin routes). The SPA does not own a
 * client-side router subtree at "/" (the blog owns "/", served by Hugo), so
 * instead of a react-router basename we read window.location.pathname once on
 * mount and pick the initial tab from it. Only the exact path "/about" maps to
 * the About tab; "/chat", admin-shell entry, and anything else default to Chat.
 *
 * Guarded for SSR/test contexts where window may be undefined.
 */
export function getInitialTab() {
  if (typeof window === 'undefined') {
    return 'chat';
  }
  if (window.location.pathname === '/about') return 'about';
  if (window.location.pathname === '/sleemai') return 'sleemai';
  return 'chat';
}

function App() {
  return (
    <Router>
      <Routes>
        {/* Public portfolio site. The blog owns "/" on the server; React is
            served at /chat and /about (and "/" only in local dev). All three
            render PortfolioSite, which seeds the initial tab from the URL. */}
        <Route path="/" element={<PortfolioSite />} />
        <Route path="/chat" element={<PortfolioSite />} />
        <Route path="/about" element={<PortfolioSite />} />
        <Route path="/sleemai" element={<PortfolioSite />} />

        {/* Hidden admin routes - not linked from public navigation.
            F100.1: /azzmin-access is now the 3-tab AdminShell. */}
        <Route path="/azzmin-login" element={<AdminLoginPage />} />
        <Route path="/azzmin-access" element={<AdminShell />} />
      </Routes>
    </Router>
  );
}

/**
 * Main Portfolio Site - The public-facing site
 */
export function PortfolioSite() {
  // F3.5: seed the initial tab from the served URL so /about boots on About
  // and /chat boots on Chat. After mount the tab buttons still drive
  // setActiveTab in memory, unchanged.
  // Tab is seeded from the URL on load. Switching is done via real links to
  // /chat and /about (the same hrefs the blog nav uses), so no in-memory setter.
  const [activeTab] = useState(getInitialTab());

  // F100.1: one page-view event per load (no-ops on localhost or when the
  // admin opt-out flag is set).
  useEffect(() => {
    trackPageView();
  }, []);

  return (
    <ThemeProvider>
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col transition-colors">
      {/* Unified header: matches the Hugo blog. Brand (memoji + name) on the
          left, link bar + theme toggle + LinkedIn on the right. Chat/About are
          real links to the server routes /chat and /about, the same hrefs the
          blog nav uses, so the two surfaces read as one site. */}
      <header className="sticky top-0 z-20 bg-slate-50 dark:bg-slate-900 px-3 pt-3 pb-2 transition-colors">
        {/* Mobile sizing (F100.1): the 4th nav item (SleemAI) clipped off-screen
            on phones - tighter gaps/padding + smaller text below md. */}
        <div className="max-w-[1080px] mx-auto rounded-full border border-slate-200 dark:border-[#2e2e2e] bg-white dark:bg-[#242424] px-3 md:px-6 shadow-sm transition-colors">
          <div className="flex items-center justify-between h-16">
            {/* Left: brand (memoji -> About Raja, wordmark -> blog home) + nav */}
            <div className="flex items-center gap-2.5 md:gap-12 min-w-0">
              <div className="flex items-center gap-1.5 md:gap-2">
                <a href="/about" aria-label="About Raja" className="w-7 h-7 md:w-8 md:h-8 rounded-full overflow-hidden inline-flex shrink-0">
                  <img src={memoji} alt="Raja Sleem" className="w-full h-full object-cover" />
                </a>
                <a href="/" className="text-base md:text-xl font-bold font-heading text-slate-900 dark:text-slate-50 whitespace-nowrap">
                  Raja Sleem
                </a>
              </div>

              <nav className="flex items-center gap-2.5 md:gap-5 text-sm md:text-base">
                <a
                  href="/"
                  className="font-medium text-slate-600 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 transition-colors whitespace-nowrap"
                >
                  Blog
                </a>
                <a
                  href="/chat"
                  className={`font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'chat'
                      ? 'text-sky-600 dark:text-sky-400 border-b-2 border-sky-500'
                      : 'text-slate-600 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400'
                  }`}
                >
                  {/* Short label on phones, full brand label from tablet up */}
                  <span className="md:hidden">Chat</span>
                  <span className="hidden md:inline">Raja Chat AI</span>
                </a>
                <a
                  href="/about"
                  className={`font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'about'
                      ? 'text-sky-600 dark:text-sky-400 border-b-2 border-sky-500'
                      : 'text-slate-600 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400'
                  }`}
                >
                  <span className="md:hidden">About</span>
                  <span className="hidden md:inline">About Raja</span>
                </a>
                <a
                  href="/sleemai"
                  className={`font-medium transition-colors whitespace-nowrap ${
                    activeTab === 'sleemai'
                      ? 'text-sky-600 dark:text-sky-400 border-b-2 border-sky-500'
                      : 'text-slate-600 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400'
                  }`}
                >
                  SleemAI
                </a>
              </nav>
            </div>

            {/* Right: theme toggle, far right */}
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {activeTab === 'chat' && <ChatTab />}
        {activeTab === 'about' && <AboutTab />}
        {activeTab === 'sleemai' && <SleemAI />}
      </main>

      {/* Footer */}
      <footer className="py-5 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 transition-colors">
        <div className="max-w-[1072px] mx-auto px-6 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-slate-500 dark:text-slate-400">
          <a
            href="https://www.linkedin.com/in/rajasleem/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
          >
            <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
            <span>Connect on LinkedIn</span>
          </a>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <span>Built with Claude Code · React · FastAPI · Hugo</span>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <span className="text-slate-400 dark:text-slate-500">v{VERSION}</span>
        </div>
      </footer>
    </div>
    </ThemeProvider>
  );
}

/**
 * Chat Tab - Main focus of the site
 */
function ChatTab() {
  return (
    <div className="h-full py-6 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Brief intro */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-slate-50 mb-2">
            Ask Me Anything
          </h2>
          <p className="text-slate-600 dark:text-slate-300">
            I'm an AI trained on Raja's experience. Ask about his skills, projects, or background.
          </p>
        </div>

        {/* Chat Container */}
        <ChatContainer />
      </div>
    </div>
  );
}

/**
 * About Tab - Background information
 */
function AboutTab() {
  return (
    <div>
      <About />
      <WhatIBring />
    </div>
  );
}

export default App;
